import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardStats, getRecentOrders, getMonthlyRevenue, getTopCustomers } from '../services/api';
import Navbar from '../components/Navbar';
import Modal from '../components/Modal';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement, 
  PointElement, 
  ArcElement, 
  Title, 
  Tooltip, 
  Legend,
  Filler 
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import '../styles/Dashboard.css';
import { formatCurrency, formatDate } from '../utils/formatters';
import api from '../services/api';

// Dashboard-specific API calls
const getFilteredStats = (params) => api.get('/dashboard/filtered-stats', { params });
const getDashboardOverdueOrders = () => api.get('/dashboard/overdue-orders');
const getDashboardUpcomingDeliveries = () => api.get('/dashboard/upcoming-deliveries');
const getDashboardActiveOrders = (params) => api.get('/dashboard/active-orders', { params });
const getDashboardCompletedOrders = (params) => api.get('/dashboard/completed-orders', { params });

ChartJS.register(
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement, 
  PointElement, 
  ArcElement, 
  Title, 
  Tooltip, 
  Legend,
  Filler
);

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [monthlyData, setMonthlyData] = useState(null);
  const [topCustomers, setTopCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [dateRange, setDateRange] = useState('thisMonth');
  
  const [filteredStats, setFilteredStats] = useState({
    orderCount: 0,
    totalRevenue: 0,
    totalPaid: 0,
    totalRemaining: 0,
    statusBreakdown: {},
    paymentBreakdown: { paid: 0, partial: 0, unpaid: 0 }
  });
  const [overdueOrders, setOverdueOrders] = useState([]);
  const [upcomingDeliveries, setUpcomingDeliveries] = useState([]);

  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalOrders, setModalOrders] = useState([]);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  useEffect(() => {
    fetchFilteredData();
  }, [dateRange]);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes, monthlyRes, topCustomersRes] = await Promise.all([
        getDashboardStats(),
        getRecentOrders(5),
        getMonthlyRevenue(selectedYear),
        getTopCustomers({ limit: 5 }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data);
      setMonthlyData(monthlyRes.data);
      setTopCustomers(topCustomersRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredData = async () => {
    try {
      const [filteredRes, overdueRes, upcomingRes] = await Promise.all([
        getFilteredStats({ date_range: dateRange }),
        getDashboardOverdueOrders(),
        getDashboardUpcomingDeliveries()
      ]);
      const fd = filteredRes.data;
      setFilteredStats({
        orderCount: fd.order_count,
        totalRevenue: fd.total_revenue,
        totalPaid: fd.total_paid,
        totalRemaining: fd.total_remaining,
        statusBreakdown: fd.status_breakdown,
        paymentBreakdown: fd.payment_breakdown || { paid: 0, partial: 0, unpaid: 0 }
      });
      setOverdueOrders(overdueRes.data);
      setUpcomingDeliveries(upcomingRes.data);
    } catch (error) {
      console.error('Error fetching filtered data:', error);
    }
  };

  const getDaysRemaining = (deliveryDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const delivery = new Date(deliveryDate);
    delivery.setHours(0, 0, 0, 0);
    const diffTime = delivery - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getDaysLabel = (days) => {
    if (days < 0) return { text: `${Math.abs(days)} gün gecikmiş`, class: 'overdue' };
    if (days === 0) return { text: 'Bugün', class: 'today' };
    if (days === 1) return { text: 'Yarın', class: 'tomorrow' };
    if (days <= 3) return { text: `${days} gün`, class: 'urgent' };
    return { text: `${days} gün`, class: 'normal' };
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'siparis_alindi': 'Sipariş Alındı',
      'uretimde': 'Üretimde',
      'teslim_edildi': 'Teslim Edildi'
    };
    return statusLabels[status] || status;
  };

  const getDateRangeLabel = () => {
    const labels = {
      'today': 'Bugün',
      'thisWeek': 'Bu Hafta',
      'thisMonth': 'Bu Ay',
      'lastMonth': 'Geçen Ay',
      'thisYear': 'Bu Yıl',
      'all': 'Tüm Zamanlar'
    };
    return labels[dateRange];
  };

  const showActiveOrders = async () => {
    try {
      const res = await getDashboardActiveOrders({ date_range: dateRange });
      setModalTitle(`Aktif Siparişler (${res.data.length})`);
      setModalOrders(res.data);
      setShowOrdersModal(true);
    } catch (e) {
      console.error(e);
    }
  };

  const showCompletedOrders = async () => {
    try {
      const res = await getDashboardCompletedOrders({ date_range: dateRange });
      setModalTitle(`Tamamlanan Siparişler (${res.data.length})`);
      setModalOrders(res.data);
      setShowOrdersModal(true);
    } catch (e) {
      console.error(e);
    }
  };

  const goToOrderDetail = (orderId) => {
    setShowOrdersModal(false);
    navigate('/orders', { state: { openOrderId: orderId } });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Dashboard yükleniyor...</p>
      </div>
    );
  }

  const paymentBreakdown = filteredStats.paymentBreakdown;

  const statusColors = {
    'siparis_alindi': { bg: 'rgba(255, 193, 7, 0.85)', border: 'rgba(255, 193, 7, 1)' },
    'uretimde': { bg: 'rgba(33, 150, 243, 0.85)', border: 'rgba(33, 150, 243, 1)' },
    'teslim_edildi': { bg: 'rgba(76, 175, 80, 0.85)', border: 'rgba(76, 175, 80, 1)' },
  };

  const orderedStatuses = ['siparis_alindi', 'uretimde', 'teslim_edildi']
    .filter(status => filteredStats.statusBreakdown[status] !== undefined);

  const statusData = {
    labels: orderedStatuses.map(status => getStatusLabel(status)),
    datasets: [{
      label: 'Sipariş Sayısı',
      data: orderedStatuses.map(status => filteredStats.statusBreakdown[status] || 0),
      backgroundColor: orderedStatuses.map(status => statusColors[status]?.bg || 'rgba(156, 156, 156, 0.85)'),
      borderColor: orderedStatuses.map(status => statusColors[status]?.border || 'rgba(156, 156, 156, 1)'),
      borderWidth: 2,
    }]
  };

  const paymentStatusData = {
    labels: ['Tam Ödendi', 'Kısmi Ödeme', 'Ödenmedi'],
    datasets: [{
      data: [paymentBreakdown.paid, paymentBreakdown.partial, paymentBreakdown.unpaid],
      backgroundColor: [
        'rgba(76, 175, 80, 0.85)',
        'rgba(255, 193, 7, 0.85)',
        'rgba(244, 67, 54, 0.85)',
      ],
      borderColor: [
        'rgba(76, 175, 80, 1)',
        'rgba(255, 193, 7, 1)',
        'rgba(244, 67, 54, 1)',
      ],
      borderWidth: 2,
    }]
  };

  const revenueData = {
    labels: monthlyData?.map(m => m.month_name) || [],
    datasets: [{
      label: 'Gelir (₺)',
      data: monthlyData?.map(m => m.total_revenue) || [],
      backgroundColor: (context) => {
        const ctx = context.chart.ctx;
        const gradient = ctx.createLinearGradient(0, 0, 0, 300);
        gradient.addColorStop(0, 'rgba(102, 126, 234, 0.4)');
        gradient.addColorStop(1, 'rgba(102, 126, 234, 0.0)');
        return gradient;
      },
      borderColor: 'rgba(102, 126, 234, 1)',
      borderWidth: 3,
      fill: true,
      tension: 0.4,
      pointBackgroundColor: 'rgba(102, 126, 234, 1)',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 5,
      pointHoverRadius: 7,
    }]
  };

  const orderCountData = {
    labels: monthlyData?.map(m => m.month_name) || [],
    datasets: [{
      label: 'Sipariş Sayısı',
      data: monthlyData?.map(m => m.order_count) || [],
      backgroundColor: (context) => {
        const ctx = context.chart.ctx;
        const gradient = ctx.createLinearGradient(0, 0, 0, 300);
        gradient.addColorStop(0, 'rgba(118, 75, 162, 0.8)');
        gradient.addColorStop(1, 'rgba(102, 126, 234, 0.8)');
        return gradient;
      },
      borderColor: 'rgba(118, 75, 162, 1)',
      borderWidth: 0,
      borderRadius: 8,
      borderSkipped: false,
      barThickness: monthlyData?.length === 1 ? 60 : undefined,
      maxBarThickness: 80,
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, padding: 20, font: { size: 12, weight: '500' } }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 },
        cornerRadius: 8,
        displayColors: true,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          font: { size: 11 },
          callback: function(value) {
            if (value >= 1000) return (value / 1000).toFixed(0) + 'K';
            return value;
          }
        }
      },
      x: { grid: { display: false }, ticks: { font: { size: 11 } } }
    },
    interaction: { intersect: false, mode: 'index' },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'right',
        labels: { usePointStyle: true, padding: 15, font: { size: 12, weight: '500' } }
      },
      tooltip: { backgroundColor: 'rgba(0, 0, 0, 0.8)', padding: 12, cornerRadius: 8 }
    }
  };

  const barOptions = {
    ...chartOptions,
    plugins: { ...chartOptions.plugins, legend: { display: false } },
    barThickness: monthlyData?.length > 6 ? undefined : 60,
    maxBarThickness: 80,
  };

  const lineOptions = {
    ...chartOptions,
    elements: {
      point: {
        radius: monthlyData?.length === 1 ? 8 : 5,
        hoverRadius: monthlyData?.length === 1 ? 10 : 7,
      }
    }
  };

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }

  return (
    <div className="dashboard-container">
      <Navbar />

      <div className="dashboard-content">
        <div className="dashboard-header">
          <div>
            <h2>Dashboard</h2>
            <p className="dashboard-subtitle"></p>
          </div>
          
          <div className="dashboard-filters">
            <div className="filter-group">
              <label>Dönem:</label>
              <select 
                value={dateRange} 
                onChange={(e) => setDateRange(e.target.value)}
                className="filter-select"
              >
                <option value="today">Bugün</option>
                <option value="thisWeek">Bu Hafta</option>
                <option value="thisMonth">Bu Ay</option>
                <option value="lastMonth">Geçen Ay</option>
                <option value="thisYear">Bu Yıl</option>
                <option value="all">Tüm Zamanlar</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Grafik Yılı:</label>
              <select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="filter-select"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* KPI Kartları */}
        <div className="stats-grid">
          <div className="stat-card stat-card-primary">
            <div className="stat-icon">📦</div>
            <div className="stat-content">
              <h3>Toplam Sipariş</h3>
              <p className="stat-value">{filteredStats.orderCount}</p>
              <span className="stat-sub">{getDateRangeLabel()}</span>
            </div>
          </div>
          <div className="stat-card stat-card-success">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <h3>Toplam Gelir</h3>
              <p className="stat-value">{formatCurrency(filteredStats.totalRevenue)}</p>
              <span className="stat-sub">{getDateRangeLabel()}</span>
            </div>
          </div>
          <div className="stat-card stat-card-info">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>Tahsil Edilen</h3>
              <p className="stat-value">{formatCurrency(filteredStats.totalPaid)}</p>
              <span className="stat-sub">{getDateRangeLabel()}</span>
            </div>
          </div>
          <div className="stat-card stat-card-warning">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <h3>Bekleyen Alacak</h3>
              <p className="stat-value">{formatCurrency(filteredStats.totalRemaining)}</p>
              <span className="stat-sub">{getDateRangeLabel()}</span>
            </div>
          </div>
        </div>

        {/* Özet Kartlar */}
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-header">
              <span className="summary-title">Tahsilat Oranı</span>
              <span className="summary-icon">📊</span>
            </div>
            <div className="summary-value">
              {filteredStats.totalRevenue > 0 
                ? ((filteredStats.totalPaid / filteredStats.totalRevenue) * 100).toFixed(1) 
                : 0}%
            </div>
            <div className="summary-bar">
              <div 
                className="summary-bar-fill" 
                style={{ 
                  width: `${filteredStats.totalRevenue > 0 
                    ? Math.min((filteredStats.totalPaid / filteredStats.totalRevenue) * 100, 100)
                    : 0}%` 
                }}
              ></div>
            </div>
            <span className="summary-sub">{getDateRangeLabel()}</span>
          </div>

          <div className="summary-card clickable" onClick={showCompletedOrders} title="Tıklayarak listeyi görüntüle">
            <div className="summary-header">
              <span className="summary-title">Tamamlanan Siparişler</span>
              <span className="summary-icon">✔️</span>
            </div>
            <div className="summary-value">
              {filteredStats.statusBreakdown?.teslim_edildi || 0}
              <span className="summary-total"> / {filteredStats.orderCount}</span>
            </div>
            <div className="summary-bar">
              <div 
                className="summary-bar-fill success" 
                style={{ 
                  width: `${filteredStats.orderCount > 0 
                    ? ((filteredStats.statusBreakdown?.teslim_edildi || 0) / filteredStats.orderCount) * 100 
                    : 0}%` 
                }}
              ></div>
            </div>
            <div className="summary-footer">
              <span className="summary-sub clickable-hint">🔍 Listele</span>
              <span className="summary-sub">{getDateRangeLabel()}</span>
            </div>
          </div>

          <div className="summary-card clickable" onClick={showActiveOrders} title="Tıklayarak listeyi görüntüle">
            <div className="summary-header">
              <span className="summary-title">Aktif Siparişler</span>
              <span className="summary-icon">🔄</span>
            </div>
            <div className="summary-value">
              {(filteredStats.statusBreakdown?.siparis_alindi || 0) + 
               (filteredStats.statusBreakdown?.uretimde || 0)}
            </div>
            <p className="summary-detail">
              Bekleyen: {filteredStats.statusBreakdown?.siparis_alindi || 0} | 
              Üretimde: {filteredStats.statusBreakdown?.uretimde || 0}
            </p>
            <div className="summary-footer">
              <span className="summary-sub clickable-hint">🔍 Listele</span>
              <span className="summary-sub">{getDateRangeLabel()}</span>
            </div>
          </div>
        </div>

        {/* Grafikler - İlk Satır */}
        <div className="charts-grid">
          <div className="chart-card">
            <div className="chart-header">
              <h3>📈 Aylık Gelir Trendi</h3>
              <span className="chart-year">{selectedYear}</span>
            </div>
            <div className="chart-container">
              {monthlyData?.length > 0 ? (
                <Line data={revenueData} options={lineOptions} />
              ) : (
                <div className="no-chart-data">Henüz veri yok</div>
              )}
            </div>
          </div>
          <div className="chart-card">
            <div className="chart-header">
              <h3>📊 Sipariş Durumları</h3>
              <span className="chart-subtitle">{getDateRangeLabel()}</span>
            </div>
            <div className="chart-container">
              <Doughnut data={statusData} options={doughnutOptions} />
            </div>
          </div>
        </div>

        {/* Grafikler - İkinci Satır */}
        <div className="charts-grid">
          <div className="chart-card">
            <div className="chart-header">
              <h3>💳 Ödeme Durumu Dağılımı</h3>
              <span className="chart-subtitle">{getDateRangeLabel()}</span>
            </div>
            <div className="chart-container">
              <Doughnut data={paymentStatusData} options={doughnutOptions} />
            </div>
          </div>
          <div className="chart-card">
            <div className="chart-header">
              <h3>📦 Aylık Sipariş Sayıları</h3>
              <span className="chart-year">{selectedYear}</span>
            </div>
            <div className="chart-container">
              <Bar data={orderCountData} options={barOptions} />
            </div>
          </div>
        </div>
        
        {/* Yaklaşan Teslimler Uyarısı */}
        {(overdueOrders.length > 0 || upcomingDeliveries.length > 0) && (
          <div className="delivery-alerts">
            {overdueOrders.length > 0 && (
              <div className="alert-card alert-danger">
                <div className="alert-header">
                  <span className="alert-icon">⚠️</span>
                  <h3>Gecikmiş Siparişler ({overdueOrders.length})</h3>
                </div>
                <div className="alert-list">
                  {overdueOrders.slice(0, 3).map(order => {
                    const days = getDaysRemaining(order.delivery_date);
                    const label = getDaysLabel(days);
                    return (
                      <div key={order.id} className="alert-item" onClick={() => navigate('/orders')}>
                        <div className="alert-item-info">
                          <span className="alert-item-id">#{order.id}</span>
                          <span className="alert-item-customer">{order.customer_name}</span>
                        </div>
                        <span className={`days-badge ${label.class}`}>{label.text}</span>
                      </div>
                    );
                  })}
                  {overdueOrders.length > 3 && (
                    <div className="alert-more" onClick={() => navigate('/orders')}>
                      +{overdueOrders.length - 3} sipariş daha →
                    </div>
                  )}
                </div>
              </div>
            )}

            {upcomingDeliveries.length > 0 && (
              <div className="alert-card alert-warning">
                <div className="alert-header">
                  <span className="alert-icon">📅</span>
                  <h3>Yaklaşan Teslimler ({upcomingDeliveries.length})</h3>
                </div>
                <div className="alert-list">
                  {upcomingDeliveries.map(order => {
                    const days = getDaysRemaining(order.delivery_date);
                    const label = getDaysLabel(days);
                    return (
                      <div key={order.id} className="alert-item" onClick={() => navigate('/orders')}>
                        <div className="alert-item-info">
                          <span className="alert-item-id">#{order.id}</span>
                          <span className="alert-item-customer">{order.customer_name}</span>
                        </div>
                        <span className={`days-badge ${label.class}`}>{label.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Alt Bölüm */}
        <div className="bottom-section">
          <div className="recent-section">
            <div className="section-header">
              <h3>🕐 Son Siparişler</h3>
              <button className="btn-link" onClick={() => navigate('/orders')}>Tümünü Gör →</button>
            </div>
            <div className="orders-table">
              <table>
                <thead>
                  <tr>
                    <th>Müşteri</th>
                    <th>Durum</th>
                    <th>Tutar</th>
                    <th>Tarih</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map(order => (
                    <tr key={order.id} onClick={() => navigate('/orders')} style={{cursor: 'pointer'}}>
                      <td title={`#${order.id}`}>{order.customer_name}</td>
                      <td>
                        <span className={`status-badge status-${order.status}`}>
                          {getStatusLabel(order.status)}
                        </span>
                      </td>
                      <td className="amount">{formatCurrency(order.total_price)}</td>
                      <td>{formatDate(order.order_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="top-customers-section">
            <div className="section-header">
              <h3>⭐ En İyi Müşteriler</h3>
              <button className="btn-link" onClick={() => navigate('/customers')}>Tümünü Gör →</button>
            </div>
            <div className="customers-list">
              {topCustomers.length > 0 ? (
                topCustomers.map((customer, index) => (
                  <div key={customer.customer_id || index} className="customer-item">
                    <div className="customer-rank">#{index + 1}</div>
                    <div className="customer-info">
                      <span className="customer-name">{customer.customer_name}</span>
                      <span className="customer-orders">{customer.order_count} sipariş</span>
                    </div>
                    <div className="customer-revenue">{formatCurrency(customer.total_revenue)}</div>
                  </div>
                ))
              ) : (
                <p className="no-data">Henüz müşteri verisi yok</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sipariş Listesi Modalı */}
      <Modal
        isOpen={showOrdersModal}
        onClose={() => setShowOrdersModal(false)}
        title={modalTitle}
        size="large"
      >
        <div className="modal-orders-list">
          {modalOrders.length > 0 ? (
            <table className="modal-orders-table">
              <thead>
                <tr>
                  <th>Müşteri</th>
                  <th>İş Tipi</th>
                  <th>Durum</th>
                  <th>Tutar</th>
                  <th>Ödenen</th>
                  <th>Tarih</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {modalOrders.map(order => (
                  <tr key={order.id}>
                    <td title={`#${order.id}`}>{order.customer_name || '-'}</td>
                    <td>{order.job_type || '-'}</td>
                    <td>
                      <span className={`status-badge status-${order.status}`}>
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td>{formatCurrency(order.total_price)}</td>
                    <td>{formatCurrency(order.total_paid)}</td>
                    <td>{formatDate(order.order_date)}</td>
                    <td>
                      <button 
                        className="btn-view-small"
                        onClick={() => goToOrderDetail(order.id)}
                        title="Siparişlere Git"
                      >
                        →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="no-data">Bu kategoride sipariş bulunmuyor</p>
          )}
          
          <div className="modal-footer">
            <button 
              className="btn btn-primary"
              onClick={() => { setShowOrdersModal(false); navigate('/orders'); }}
            >
              Tüm Siparişlere Git
            </button>
          </div>
        </div>

        <style>{`
          .modal-orders-list { max-height: 500px; overflow-y: auto; }
          .modal-orders-table { width: 100%; border-collapse: collapse; }
          .modal-orders-table th, .modal-orders-table td { padding: 12px 10px; text-align: left; border-bottom: 1px solid #eee; font-size: 13px; }
          .modal-orders-table th { background: #f8f9fa; font-weight: 600; color: #333; }
          .modal-orders-table tr:hover { background: #f5f5f5; }
          .btn-view-small { background: linear-gradient(135deg, #667eea, #764ba2); color: white; border: none; padding: 5px 12px; border-radius: 5px; cursor: pointer; font-size: 14px; }
          .modal-footer { margin-top: 20px; padding-top: 15px; border-top: 1px solid #eee; text-align: center; }
        `}</style>
      </Modal>
    </div>
  );
}

export default Dashboard;